import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/ui/Modal';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Shield } from 'lucide-react';

const ALL_PERMS = ['books', 'videos', 'cities', 'users', 'broadcast', 'admins', 'settings', 'logs'];

function safePerms(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { return JSON.parse(val); } catch(e) { return []; }
}

function safeDate(val) {
  if (!val) return '—';
  try { return new Date(val).toLocaleString('en-PK'); } catch(e) { return '—'; }
}

export default function Admins() {
  const api = useApi();
  const { can, user: me } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', role: 'read_only', permissions: [], is_active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const d = await api.get('/users/admins').catch(() => null);
    if (d && !d.error) setAdmins(d.users || []);
    else if (d?.error) toast.error(d.error);
    setLoading(false);
  }

  function openAdd() {
    setForm({ username: '', password: '', role: 'read_only', permissions: [], is_active: true });
    setModal('add');
  }

  function openEdit(a) {
    setForm({
      username: a.username,
      password: '',
      role: a.role,
      permissions: safePerms(a.permissions),
      is_active: a.is_active
    });
    setModal(a);
  }

  function togglePerm(p) {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(p)
        ? prev.permissions.filter(x => x !== p)
        : [...prev.permissions, p]
    }));
  }

  async function save() {
    if (!form.username.trim()) return toast.error('Username likhein');
    if (modal === 'add' && !form.password.trim()) return toast.error('Password likhein');
    setSaving(true);
    try {
      if (modal === 'add') {
        const d = await api.post('/users/admins', form);
        if (d?.user) { toast.success('Admin add ho gaya!'); load(); setModal(null); }
        else toast.error(d?.error || 'Error hua');
      } else {
        const payload = { role: form.role, permissions: form.permissions, is_active: form.is_active };
        if (form.password.trim()) payload.password = form.password;
        const d = await api.put('/users/' + modal.id, payload);
        if (d?.success) { toast.success('Update ho gaya!'); load(); setModal(null); }
        else toast.error(d?.error || 'Error hua');
      }
    } catch(e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  }

  async function del(a) {
    if (a.id === me?.id) return toast.error('Apne aap ko delete nahi kar sakte');
    if (!window.confirm('"' + a.username + '" delete karein?')) return;
    const d = await api.del('/users/' + a.id);
    if (d?.success) { toast.success('Delete ho gaya'); load(); }
    else toast.error(d?.error || 'Delete nahi hua');
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="section-title">🛡️ Admin Users</div>
          <div className="section-sub">Panel ke admin accounts aur permissions manage karein</div>
        </div>
        {can('admins') && (
          <button className="btn btn-primary" onClick={openAdd}>
            <Plus size={14} /> Naya Admin
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" style={{ width: 24, height: 24 }} /></div>
        ) : admins.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            <Shield size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
            <div>Koi admin nahi mila</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Username', 'Role', 'Permissions', 'Status', 'Last Login', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {admins.map(a => (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {a.username}
                      {a.id === me?.id && (
                        <span style={{ marginLeft: 6, background: 'var(--gold-900)', border: '1px solid var(--gold-700)', color: 'var(--gold-300)', padding: '1px 6px', borderRadius: 10, fontSize: 10 }}>Aap</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        background: a.role === 'super_admin' ? 'var(--gold-900)' : 'var(--bg-surface)',
                        border: '1px solid ' + (a.role === 'super_admin' ? 'var(--gold-700)' : 'var(--border)'),
                        color: a.role === 'super_admin' ? 'var(--gold-300)' : 'var(--text-muted)',
                        padding: '2px 8px', borderRadius: 12, fontSize: 11
                      }}>
                        {a.role === 'super_admin' ? '👑 Super Admin' : '👤 Read Only'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {a.role === 'super_admin' ? (
                        <span style={{ background: 'var(--emerald-900)', border: '1px solid var(--emerald-700)', color: 'var(--emerald-300)', padding: '2px 8px', borderRadius: 12, fontSize: 11 }}>All Access</span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {safePerms(a.permissions).map(p => (
                            <span key={p} style={{ background: 'var(--emerald-900)', border: '1px solid var(--emerald-700)', color: 'var(--emerald-300)', padding: '1px 6px', borderRadius: 10, fontSize: 10 }}>{p}</span>
                          ))}
                          {safePerms(a.permissions).length === 0 && (
                            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Koi permission nahi</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        background: a.is_active ? 'var(--emerald-900)' : 'var(--danger-bg)',
                        border: '1px solid ' + (a.is_active ? 'var(--emerald-700)' : 'var(--danger)'),
                        color: a.is_active ? 'var(--emerald-300)' : 'var(--danger)',
                        padding: '2px 8px', borderRadius: 12, fontSize: 11
                      }}>
                        {a.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 11, color: 'var(--text-muted)' }}>{safeDate(a.last_login)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {can('admins') && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => openEdit(a)} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', padding: '4px 8px', borderRadius: 6, cursor: 'pointer' }} title="Edit">
                            <Pencil size={13} />
                          </button>
                          {a.id !== me?.id && (
                            <button onClick={() => del(a)} style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', color: 'var(--danger)', padding: '4px 8px', borderRadius: 6, cursor: 'pointer' }} title="Delete">
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <Modal
          title={modal === 'add' ? '🛡️ Naya Admin Add Karein' : '✏️ Admin Edit Karein'}
          onClose={() => setModal(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving && <span className="spinner" style={{ width: 14, height: 14, marginRight: 6 }} />}
                {modal === 'add' ? 'Add Karein' : 'Save Karein'}
              </button>
            </>
          }
        >
          <div className="field">
            <label className="label">Username *</label>
            <input className="input" placeholder="admin2" value={form.username}
              onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
              disabled={modal !== 'add'} autoFocus />
          </div>
          <div className="field">
            <label className="label">{modal === 'add' ? 'Password *' : 'Naya Password (khali chhorain agar nahi badalna)'}</label>
            <input className="input" type="password" placeholder="••••••••" value={form.password}
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
          </div>
          <div className="field">
            <label className="label">Role</label>
            <select className="select" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
              <option value="read_only">👤 Read Only</option>
              <option value="super_admin">👑 Super Admin</option>
            </select>
          </div>
          {form.role !== 'super_admin' && (
            <div className="field">
              <label className="label">Permissions</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {ALL_PERMS.map(p => {
                  const active = form.permissions.includes(p);
                  return (
                    <span key={p} onClick={() => togglePerm(p)} style={{
                      padding: '4px 10px', borderRadius: 14, fontSize: 12, cursor: 'pointer',
                      background: active ? 'var(--emerald-800)' : 'var(--bg-surface)',
                      border: '1px solid ' + (active ? 'var(--emerald-600)' : 'var(--border)'),
                      color: active ? 'var(--emerald-200)' : 'var(--text-muted)',
                      userSelect: 'none', transition: 'all 0.15s'
                    }}>
                      {active ? '✓ ' : ''}{p}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label className="label" style={{ margin: 0 }}>Status:</label>
            <input type="checkbox" checked={form.is_active}
              onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))}
              style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <span style={{ fontSize: 12, color: form.is_active ? 'var(--emerald-300)' : 'var(--text-muted)' }}>
              {form.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </Modal>
      )}
    </div>
  );
}
